import { useRef } from 'react'
import { Camera, ImagePlus, Paperclip } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { getAcceptForUseCase } from '@/shared/lib/file-types'

interface CaptureButtonProps {
  kind: 'photo' | 'document'
  disabled?: boolean
  onFiles: (files: File[]) => void
}

/**
 * "Take Photo" / "Choose from Library" (photo) or "Upload Document" (document)
 * capture controls. Shared by the Upload Evidence page and the consumer claim
 * evidence checklist (WPS-138 #6).
 */
export function CaptureButton({ kind, disabled, onFiles }: CaptureButtonProps) {
  const cameraRef = useRef<HTMLInputElement>(null)
  const libraryRef = useRef<HTMLInputElement>(null)
  const docRef = useRef<HTMLInputElement>(null)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (files.length > 0) onFiles(files)
    e.target.value = ''
  }

  if (kind === 'document') {
    return (
      <>
        <input
          ref={docRef}
          type="file"
          accept={getAcceptForUseCase('CLAIM_EVIDENCE')}
          multiple
          className="hidden"
          onChange={handleChange}
        />
        <Button
          variant="outline"
          size="lg"
          className="h-12 w-full gap-2.5 border-[0.5px] border-[var(--rule-strong)] text-[12px] tracking-[0.06em]"
          disabled={disabled}
          onClick={() => docRef.current?.click()}
        >
          <Paperclip size={16} />
          Upload Document
        </Button>
      </>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        className="hidden"
        onChange={handleChange}
      />
      <input
        ref={libraryRef}
        type="file"
        // The library pick is "choose a file": a homeowner's evidence is often
        // a PDF, a video or an email, not a photo (WPS-190). The camera stays
        // photo-only.
        accept={getAcceptForUseCase('CLAIM_EVIDENCE')}
        multiple
        className="hidden"
        onChange={handleChange}
      />

      <Button
        size="lg"
        className="h-12 w-full gap-2.5 bg-[var(--green)] text-[12px] tracking-[0.06em] text-white hover:bg-[var(--green-hover)]"
        disabled={disabled}
        onClick={() => cameraRef.current?.click()}
      >
        <Camera size={16} />
        Take Photo
      </Button>

      <Button
        variant="outline"
        size="lg"
        className="h-12 w-full gap-2.5 border-[0.5px] border-[var(--rule-strong)] text-[12px] tracking-[0.06em]"
        disabled={disabled}
        onClick={() => libraryRef.current?.click()}
      >
        <ImagePlus size={16} />
        Choose from Library
      </Button>
    </div>
  )
}
